import type { UseMutationResult } from "@tanstack/react-query";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
	CitiesResponseSchema,
	CountriesResponseSchema,
	LoginResponseSchema,
	LoginVerifyResponseSchema,
	StatesResponseSchema,
	UserInfoResponseSchema,
	ZipCodesResponseSchema,
} from "src/schema/auth-schema";
import type {
	TCitiesResponse,
	TCountryCodeResponse,
	TDeleteAccountBody,
	TDeleteAccountResponse,
	TEmailLoginBody,
	TLoginEmailVerifyBody,
	TLoginPhoneVerifyBody,
	TLoginResponse,
	TLoginVerifyResponse,
	TPhoneLoginBody,
	TProfileBody,
	TStatesResponse,
	TUserInfoResponse,
	TZipCodeResponse,
} from "src/type/auth";
import axiosInstance from "src/utils/axios";

const getCountryCodes = async (): Promise<TCountryCodeResponse> => {
	const { data } = await axiosInstance.get(`/api/common/authsystem/v1.0/country/?ordering=id`);
	const result = CountriesResponseSchema.safeParse(data);
	if (!result.success) {
		throw new Error(`Parse countries Response Error${JSON.stringify(result.error)}`);
	}
	return data;
};
const getCities = async ({
	page,
	pageSize,
	search,
	stateId,
	inDb,
}: {
	page: string;
	pageSize: number;
	stateId?: number;
	inDb?: boolean;
	search: string;
}): Promise<TCitiesResponse> => {
	// const baseUrl = nextUrl ? `${nextUrl.substring(nextUrl.lastIndexOf("/"), nextUrl.length)}` : "/user-search-trip?";
	const baseUrl =
		page !== ""
			? page
			: `/api/common/authsystem/v1.0/city/?page-size=${pageSize}&search=${search}${stateId ? `&state=${stateId}` : ""}${inDb ? `&in_db=${inDb.toString()}` : ""}`;
	const { data } = await axiosInstance.get(baseUrl);
	const result = CitiesResponseSchema.safeParse(data);
	if (!result.success) {
		throw new Error(`Parse cities Response Error${JSON.stringify(result.error)}`);
	}
	return data;
};
const getStates = async ({
	page,
	pageSize,
	search,
}: {
	page: string;
	pageSize: number;
	search: string;
}): Promise<TStatesResponse> => {
	// const baseUrl = nextUrl ? `${nextUrl.substring(nextUrl.lastIndexOf("/"), nextUrl.length)}` : "/user-search-trip?";
	const baseUrl =
		page !== "" ? page : `/api/common/authsystem/v1.0/state/?page-size=${pageSize}&search=${search}`;
	const { data } = await axiosInstance.get(baseUrl);
	const result = StatesResponseSchema.safeParse(data);
	if (!result.success) {
		throw new Error(`Parse States Response Error${JSON.stringify(result.error)}`);
	}
	return data;
};
const getUserInfo = async (): Promise<TUserInfoResponse> => {
	const { data } = await axiosInstance.get(`/api/app/authsystem/v1.0/user/info`);
	const result = UserInfoResponseSchema.safeParse(data);
	if (!result.success) {
		throw new Error(`Parse user Info Response Error${JSON.stringify(result.error)}`);
	}
	return data;
};

const getZipCodes = async ({
	search,
	cityId,
}: {
	search?: string;
	cityId?: string | null;
}): Promise<TZipCodeResponse> => {
	const { data } = await axiosInstance.get(
		`/api/common/authsystem/v1.0/zipcode/?${search ? `search=${search}` : ""}${cityId && !search ? `&city=${cityId}` : ""}`,
	);
	const result = ZipCodesResponseSchema.safeParse(data);
	if (!result.success) {
		throw new Error(`Parse zip codes Response Error${JSON.stringify(result.error)}`);
	}
	return data;
};

const updateProfile = async (body: TProfileBody): Promise<{ message: string }> => {
	const { data } = await axiosInstance.post(`/api/app/authsystem/v1.0/user/profile`, body);
	return data;
};
const postPhoneLogin = async (body: TPhoneLoginBody): Promise<TLoginResponse> => {
	const { data } = await axiosInstance.post(`api/app/authsystem/v1.0/login`, body);
	const result = LoginResponseSchema.safeParse(data);
	if (!result.success) {
		throw {
			message: `Parse  Login Phone Response Error${JSON.stringify(result.error)}`,
		};
	}
	return data;
};
const postEmailLogin = async (body: TEmailLoginBody): Promise<TLoginResponse> => {
	const { data } = await axiosInstance.post(`api/app/authsystem/v1.0/email-login`, body);
	const result = LoginResponseSchema.safeParse(data);
	if (!result.success) {
		throw {
			message: `Parse  Login Email Response Error${JSON.stringify(result.error)}`,
		};
	}
	return data;
};
const postDeleteAccount = async (body: TDeleteAccountBody): Promise<TDeleteAccountResponse> => {
	const { data } = await axiosInstance.post(`api/app/authsystem/v1.0/user/delete-account`, body);

	return data;
};

const verifyPhoneLogin = async (body: TLoginPhoneVerifyBody): Promise<TLoginVerifyResponse> => {
	const { data } = await axiosInstance.post(
		`/api/app/authsystem/v1.0/login/check_verify_code`,
		body,
	);
	const result = LoginVerifyResponseSchema.safeParse(data);
	if (!result.success) {
		throw {
			message: `Parse  Login Phone Verify Response Error${JSON.stringify(result.error)}`,
		};
	}
	return data;
};
const verifyEmailLogin = async (body: TLoginEmailVerifyBody): Promise<TLoginVerifyResponse> => {
	const { data } = await axiosInstance.post(
		`/api/app/authsystem/v1.0/email-login/check_verify_code`,
		body,
	);
	const result = LoginVerifyResponseSchema.safeParse(data);
	if (!result.success) {
		throw {
			message: `Parse  Login Email Verify Response Error${JSON.stringify(result.error)}`,
		};
	}
	return data;
};
// hooks
function useGetCountryCode() {
	return useQuery({
		queryKey: ["countryCodes"],
		queryFn: () => getCountryCodes(),
	});
}

function useGetZipCodes({
	search,
	cityId,
	enabled,
}: {
	search?: string;
	cityId?: string | null;
	enabled?: boolean;
}) {
	return useQuery({
		queryKey: ["zipCodes", search, cityId],
		queryFn: () => getZipCodes({ cityId, search }),
		enabled: !!enabled,
	});
}

function useGetCities({
	pageSize,
	search,
	stateId,
	enable = true,
	inDb = false,
}: {
	stateId?: number;
	search: string;
	pageSize: number;
	enable?: boolean;
	inDb?: boolean;
}) {
	return useInfiniteQuery({
		queryKey: ["cities", search, stateId],
		queryFn: ({ pageParam }: { pageParam: string }) =>
			getCities({ inDb, pageSize, page: pageParam, search, stateId }),
		staleTime: Infinity,
		getNextPageParam: (lastPage) => lastPage.next,
		initialPageParam: "",
		enabled: enable,
	});
}
function useGetStates({
	pageSize,
	search,
	enable = true,
}: {
	search: string;
	pageSize: number;
	enable?: boolean;
}) {
	return useInfiniteQuery({
		queryKey: ["states", search],
		queryFn: ({ pageParam }: { pageParam: string }) =>
			getStates({ pageSize, page: pageParam, search }),
		staleTime: Infinity,
		getNextPageParam: (lastPage) => lastPage.next,
		initialPageParam: "",
		enabled: enable,
	});
}

const usePostPhoneLogin = (): UseMutationResult<TLoginResponse, unknown, TPhoneLoginBody> =>
	useMutation({
		mutationFn: (body: TPhoneLoginBody) => postPhoneLogin(body),
	});
const usePostEmailLogin = (): UseMutationResult<TLoginResponse, unknown, TEmailLoginBody> =>
	useMutation({
		mutationFn: (body: TEmailLoginBody) => postEmailLogin(body),
	});

const usePostDeleteAccount = (): UseMutationResult<
	TDeleteAccountResponse,
	unknown,
	TDeleteAccountBody
> =>
	useMutation({
		mutationFn: (body: TDeleteAccountBody) => postDeleteAccount(body),
	});

const usePostVerifyLogin = (): UseMutationResult<
	TLoginVerifyResponse,
	unknown,
	TLoginPhoneVerifyBody
> =>
	useMutation({
		mutationFn: (body: TLoginPhoneVerifyBody) => verifyPhoneLogin(body),
	});

const usePostVerifyEmailLogin = (): UseMutationResult<
	TLoginVerifyResponse,
	unknown,
	TLoginEmailVerifyBody
> =>
	useMutation({
		mutationFn: (body: TLoginEmailVerifyBody) => verifyEmailLogin(body),
	});

const usePostProfile = (): UseMutationResult<{ message: string }, unknown, TProfileBody> =>
	useMutation({ mutationFn: (body: TProfileBody) => updateProfile(body) });

function useGetUserInfo({ enabled = true }: { enabled?: boolean }) {
	return useQuery({
		queryKey: ["user-info"],
		queryFn: () => getUserInfo(),
		enabled,
	});
}

export {
	useGetCities,
	useGetCountryCode,
	useGetStates,
	useGetUserInfo,
	useGetZipCodes,
	usePostDeleteAccount,
	usePostEmailLogin,
	usePostPhoneLogin,
	usePostProfile,
	usePostVerifyEmailLogin,
	usePostVerifyLogin,
};
